/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Product, ProductMetadata } from '@shared/types/products';
import { Plus, Loader, Package, LayoutGrid, Focus, Download, FileCode } from 'lucide-react';
import { getMyOrganizations } from '@shared/lib/api/user-organizations';
import type { UserOrganizationWithDetails } from '@shared/schema/organizations';
import { useAuth } from '@shared/contexts/AuthContext';
import { ProductCard } from '../components/products/ProductCard';
import { ProductForm, ProductFormData, RepoMode } from '../components/products/ProductForm';
import { ProductFilters } from '../components/products/ProductFilters';
import { ProductListHeader } from '../components/products/ProductListHeader';
import { ProductBrowser } from '../components/products/ProductBrowser';
import { ProductFocusView } from '../components/products/ProductFocusView';
import { ImportProductDialog } from '../components/products/ImportProductDialog';
import { RequestInput } from '../components/common/RequestInput';
import { useRequestProcessor } from '../hooks/useRequestProcessor';
import { SkillShopService } from '../services/skillShopService';
import { useApp } from '../contexts/AppContext';
import { getDefaultRepoRelativePath, normalizeRepoUrl } from '../utils/repoPaths';
import { getRepoContext } from '../utils/repoContext';
import { generateProductIdFromName } from '@shared/utils/products';
import { Button } from '../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { ensureProductRepos, cloneRepository as cloneRepositoryApi } from '../lib/api';

const emptyFormState: ProductFormData = {
  title: '',
  description: '',
  owner: '',
  links: [],
  capabilities: [],
  users: [],
  repositoryUrl: null,
  repoPath: null,
};

const toFormData = (product: Product): ProductFormData => ({
  id: product.id,
  productId: product.productId,
  title: product.title,
  description: product.description,
  owner: product.owner,
  links: product.links ?? [],
  capabilities: product.capabilities ?? [],
  users: product.users ?? [],
  repositoryUrl: product.repositoryUrl ?? null,
  repoPath: product.repoPath ?? null,
});

export function Products() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    githubService,
    isGitHubConfigured,
    githubConfig,
    gitlabService,
    isGitLabConfigured,
    gitlabConfig,
    sourceControlProvider,
    activeProduct,
    activeRepoUrl,
    setActiveProduct,
    setActiveRepoUrl,
    quickRequestState,
    setQuickRequest,
  } = useApp();
  const [products, setProducts] = useState<Product[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProductFormData>(emptyFormState);

  // Layout mode: 'list' or 'focus'
  const [layoutMode, setLayoutMode] = useState<'list' | 'focus'>('list');
  // Selected product for focus view (separate from activeProduct)
  const [focusedProduct, setFocusedProduct] = useState<Product | null>(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  // Repository creation state
  const [isCreatingRepo, setIsCreatingRepo] = useState(false);
  const [createRepoError, setCreateRepoError] = useState<string | null>(null);

  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);

  // User organization ID for import
  const [userOrganizationId, setUserOrganizationId] = useState<string>('');

  // Fetch user's organizations to determine which org's products to display
  const { data: userOrganizations = [], isLoading: isLoadingOrgs } = useQuery({
    ...getMyOrganizations(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
  
  // Find the default (active) organization
  // Falls back to first organization if none is explicitly marked as default
  const activeOrganization = useMemo(() => {
    if (userOrganizations.length === 0) return null;
    
    // First, try to find the explicitly marked default
    const defaultOrg = userOrganizations.find((org: UserOrganizationWithDetails) => org.isDefault);
    if (defaultOrg?.organization) {
      return defaultOrg.organization;
    }
    
    // Fallback: use the first organization in the list
    // This handles legacy data where is_default might not be set
    console.log('No default organization found, using first organization as fallback');
    return userOrganizations[0]?.organization ?? null;
  }, [userOrganizations]);

  const [skillShopService] = useState(() => new SkillShopService());
  const { toast } = useToast();

  // Ref for scrolling to form
  const formRef = useRef<HTMLDivElement>(null);

  const repoContext = useMemo(
    () => getRepoContext(activeProduct ?? null, activeRepoUrl),
    [activeProduct, activeRepoUrl],
  );

  // Use the generic request processor hook with external state for persistence across navigation
  const requestProcessor = useRequestProcessor({
    onProcess: async (request: string) => {
      const result = await skillShopService.processProductRequest(request, activeProduct);
      if (result.success && result.result) {
        requestProcessor.setResponse(result.result);
        const productsData = await loadProducts();
        syncActiveProduct(productsData);
      } else {
        throw new Error(result.error || 'Failed to process product request');
      }
    },
    onSuccess: () => {
      console.log('Product request processed successfully');
    },
    externalValue: quickRequestState.products,
    onExternalValueChange: (value) => setQuickRequest('products', value),
  });

  // Load products from database via API, filtered by active organization
  const loadProducts = useCallback(async () => {
    // Wait for organization data to be available
    if (!activeOrganization?.id) {
      console.log('Waiting for organization data before loading products');
      return [];
    }
    
    setLoadingProducts(true);
    try {
      // Fetch products for the active organization only
      const productsData = await skillShopService.getProductsByOrganization(activeOrganization.id);
      setProducts(productsData);
      console.log(`Loaded ${productsData.length} products for organization: ${activeOrganization.name}`);
      return productsData;
    } catch (error) {
      console.error('Error loading products:', error);
      return [];
    } finally {
      setLoadingProducts(false);
    }
  }, [skillShopService, activeOrganization?.id, activeOrganization?.name]);

  // Ensure product repositories exist (runs once after initial load)
  const ensureProductRepositories = useCallback(async (productsData: Product[]) => {
    if (productsData.length === 0) return;

    try {
      const accessToken = sourceControlProvider === 'github' 
        ? githubConfig?.accessToken 
        : gitlabConfig?.accessToken;
      const useSSH = sourceControlProvider === 'gitlab' && gitlabConfig?.useSSH;
      
      const ensureResult = await ensureProductRepos(
        productsData,
        accessToken,
        sourceControlProvider,
        useSSH
      );
      
      if (ensureResult.success && ensureResult.summary) {
        console.log(
          `Ensured ${ensureResult.summary.successful} repositories successfully` +
          (ensureResult.summary.failed > 0 ? `, ${ensureResult.summary.failed} failed` : '')
        );
        
        // Show toast for failures if any
        if (ensureResult.summary.failed > 0) {
          const failedProducts = ensureResult.results?.filter(r => !r.success) || [];
          const failedNames = failedProducts.map(r => r.productId).join(', ');
          toast({
            title: 'Some repositories could not be ensured',
            description: `Failed to ensure repos for: ${failedNames}`,
            variant: 'destructive',
          });
        }
      }
    } catch (error) {
      console.error('Error ensuring product repositories:', error);
    }
  }, [sourceControlProvider, githubConfig?.accessToken, gitlabConfig?.accessToken, toast]);

  // Sync active product with loaded products
  // Ensures there's always an active product when products exist
  const syncActiveProduct = useCallback((productsData: Product[]) => {
    // No products, nothing to activate
    if (productsData.length === 0) {
      if (activeProduct) {
        setActiveProduct(null);
      }
      return;
    }

    const normalizedActiveRepo = repoContext.normalizedRepoUrl;

    // If we have an active product, verify it still exists in the loaded data
    if (activeProduct) {
      const stillExists = productsData.find((p) => p.id === activeProduct.id);
      if (!stillExists) {
        // Active product was deleted, activate the first available product
        setActiveProduct(productsData[0]);
      } else {
        // Active product still exists, check if its data changed
        if (
          stillExists.repoPath !== activeProduct.repoPath ||
          stillExists.repositoryUrl !== activeProduct.repositoryUrl
        ) {
          // Product data changed, update the active product reference
          setActiveProduct(stillExists);
        }
        // Otherwise, keep the current active product as-is
      }
      return;
    }

    // No active product - we need to set one
    // First, try to match by repo URL if we have one
    if (normalizedActiveRepo) {
      const matchingProduct = productsData.find((product) => {
        if (!product.repositoryUrl) return false;
        return normalizeRepoUrl(product.repositoryUrl) === normalizedActiveRepo;
      });

      if (matchingProduct) {
        setActiveProduct(matchingProduct);
        return;
      }
    }

    // No match by repo URL, activate the first product
    // Prefer products with a repo URL, but activate any product if none have one
    const productWithRepo = productsData.find((product) => product.repositoryUrl);
    setActiveProduct(productWithRepo || productsData[0]);
  }, [activeProduct, repoContext.normalizedRepoUrl, setActiveProduct]);

  /**
   * Generate a product ID from the product name.
   * Uses the PROD-XXXX format where XXXX is derived from the product name.
   * The productId is for display purposes only and does not need to be unique.
   */
  const generateDisplayProductId = (name: string): string => {
    return generateProductIdFromName(name);
  };

  // Update userOrganizationId when active organization changes (for import functionality)
  useEffect(() => {
    if (activeOrganization?.id) {
      setUserOrganizationId(activeOrganization.id);
    }
  }, [activeOrganization?.id]);

  // Load products when active organization changes
  useEffect(() => {
    if (!activeOrganization?.id || isLoadingOrgs) {
      return;
    }

    const initialize = async () => {
      const productsData = await loadProducts();
      
      // Note: We no longer ensure ALL repos on page load for performance.
      // The active product's repo is ensured via AppContext when selected.
      // Repos are only ensured on-demand when the user interacts with them.
      
      // Sync active product with loaded data
      syncActiveProduct(productsData);
    };
    
    void initialize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization?.id, isLoadingOrgs]); // Reload when organization changes

  // When switching to focus mode, ensure we have a focused product
  useEffect(() => {
    if (layoutMode === 'focus' && !focusedProduct && products.length > 0) {
      // Default to active product or first product
      setFocusedProduct(activeProduct || products[0]);
    }
  }, [layoutMode, focusedProduct, products, activeProduct]);

  // Handle switching to focus mode for a specific product
  const handleFocusProduct = (product: Product) => {
    setFocusedProduct(product);
    setLayoutMode('focus');
  };

  // Clone repository (GitHub or GitLab)
  const cloneRepository = async (repositoryUrl: string | null) => {
    if (!repositoryUrl) return;
    
    console.log('Cloning repository:', repositoryUrl);
    
    const accessToken = sourceControlProvider === 'github' 
      ? githubConfig?.accessToken 
      : gitlabConfig?.accessToken;
    const useSSH = sourceControlProvider === 'gitlab' && gitlabConfig?.useSSH;
    
    const result = await cloneRepositoryApi(
      repositoryUrl,
      undefined, // productTitle
      null,      // repoPath
      accessToken,
      sourceControlProvider,
      useSSH
    );
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to clone repository');
    }

    console.log(`Repository ${result.action}:`, result.repository);

    if (repositoryUrl) {
      setActiveRepoUrl(repositoryUrl);
    }
    return result;
  };

  // Handle create/update product
  const handleSubmit = async (options?: { 
    repoMode?: RepoMode;
    parsedArrays?: { capabilities: string[]; users: string[]; links: string[] };
  }) => {
    if (!formData.title || !formData.description || !formData.owner) {
      toast({
        title: 'Missing required fields',
        description: 'Please provide a title, description, and owner.',
        variant: 'destructive',
      });
      return;
    }

    // For new products, ensure we have the user's organization ID
    if (!isEditing && !userOrganizationId) {
      toast({
        title: 'Organization not available',
        description: 'Unable to determine your organization. Please try again or log in.',
        variant: 'destructive',
      });
      return;
    }

    // Use parsed arrays from form if provided (ensures latest values are used)
    if (options?.parsedArrays) {
      formData.capabilities = options.parsedArrays.capabilities;
      formData.users = options.parsedArrays.users;
      formData.links = options.parsedArrays.links;
    }

    const repoMode = options?.repoMode || 'local-only';
    
    // For link-remote mode, we need the repositoryUrl set
    // For local-only mode, we clear the repositoryUrl so ensure-repos will init a local repo
    if (repoMode === 'local-only') {
      formData.repositoryUrl = null;
    }

    setIsCreatingRepo(true);
    setCreateRepoError(null);

    try {
      let repositoryUrlChanged = false;
      let previousRepoUrl: string | undefined;
      let previousRepoNormalized: string | null = null;
      const nextRepoUrl = formData.repositoryUrl ?? undefined;
      const nextRepoNormalized = nextRepoUrl ? normalizeRepoUrl(nextRepoUrl) : null;

      if (isEditing && formData.id) {
        // Check if repository URL changed
        const existingProduct = products.find(p => p.id === formData.id);
        if (!existingProduct) {
          throw new Error('Product not found');
        }

        previousRepoUrl = existingProduct.repositoryUrl ?? undefined;
        previousRepoNormalized = previousRepoUrl ? normalizeRepoUrl(previousRepoUrl) : null;
        repositoryUrlChanged = (existingProduct.repositoryUrl ?? null) !== (formData.repositoryUrl ?? null);
        
        // Note: We intentionally do NOT delete the old local repo when transitioning to a new repo.
        // The old repo may contain unsynced data that the user hasn't pushed yet.
        // Old repos can be manually cleaned up from ~/.velocity/repos if needed.
        
        // Update existing product via API
        await skillShopService.updateProduct(existingProduct.id, {
          productId: formData.productId ?? existingProduct.productId,
          title: formData.title,
          description: formData.description,
          owner: formData.owner,
          links: formData.links,
          capabilities: formData.capabilities,
          users: formData.users,
          repositoryUrl: formData.repositoryUrl ?? null,
          repoPath: formData.repoPath ?? null,
        });
      } else {
        // Create new product via API
        // Generate display product ID from title using PROD-XXXX format
        const newProductId = generateDisplayProductId(formData.title);
        await skillShopService.createProduct({
          organizationId: userOrganizationId,
          productId: newProductId,
          title: formData.title,
          description: formData.description,
          owner: formData.owner,
          links: formData.links,
          capabilities: formData.capabilities,
          users: formData.users,
          repositoryUrl: formData.repositoryUrl ?? null,
          repoPath: formData.repoPath ?? null,
        });
        repositoryUrlChanged = !!formData.repositoryUrl;
        
        // Store the new product ID for activation after reload
        formData.productId = newProductId;
      }

      // Reload products from database to get the newly created/updated product
      const updatedProducts = await loadProducts();
      
      // For new products, automatically activate them
      if (!isEditing && updatedProducts.length > 0) {
        const newProduct = updatedProducts.find(p => p.productId === formData.productId);
        if (newProduct) {
          setActiveProduct(newProduct);
        }
      }
      
      // Ensure the repository exists (clone remote or init local)
      // For new products, this is always needed
      // For edits, only if repositoryUrl changed
      if (!isEditing || repositoryUrlChanged) {
        try {
          // Get access token based on source control provider
          const accessToken = sourceControlProvider === 'github' 
            ? githubConfig?.accessToken 
            : gitlabConfig?.accessToken;
          const useSSH = sourceControlProvider === 'gitlab' && gitlabConfig?.useSSH;
          
          // Build a minimal product object for ensure-repos
          const productToEnsure = {
            id: formData.id,
            productId: formData.productId,
            title: formData.title,
            description: formData.description,
            repositoryUrl: formData.repositoryUrl,
          };
          
          const ensureResult = await ensureProductRepos(
            [productToEnsure],
            accessToken,
            sourceControlProvider,
            useSSH
          );
          
          if (ensureResult.success && ensureResult.results?.[0]) {
            const repoResult = ensureResult.results[0];
            if (repoResult.success) {
              toast({
                title: repoResult.action === 'cloned' 
                  ? 'Repository cloned' 
                  : repoResult.action === 'updated'
                    ? 'Repository updated'
                    : 'Repository ready',
                description: formData.repositoryUrl 
                  ? `Remote repository is available locally`
                  : 'Local repository initialized for this product',
              });

              // If a local repo was created (no remote URL), update the product's repositoryUrl
              if (!formData.repositoryUrl && repoResult.repositoryUrl) {
                const localRepoUrl = repoResult.repositoryUrl.startsWith('local/')
                  ? repoResult.repositoryUrl
                  : `local/${repoResult.repositoryUrl}`;
                const currentProduct = updatedProducts.find(p => p.id === formData.id || p.productId === formData.productId);
                if (currentProduct) {
                  await skillShopService.updateProduct(currentProduct.id, { repositoryUrl: localRepoUrl });
                  formData.repositoryUrl = localRepoUrl;
                  currentProduct.repositoryUrl = localRepoUrl;
                }
              }

              // After repo is ensured, sync product metadata with the repo
              // - If product.md exists in repo: sync FROM file TO database
              // - If product.md doesn't exist: write FROM database TO file
              if (formData.repositoryUrl) {
                // Find the product from the currently loaded data
                const currentProduct = updatedProducts.find(p => p.id === formData.id || p.productId === formData.productId);
                
                if (currentProduct) {
                  const syncResult = await skillShopService.syncProductMetadataOnRepoChange(
                    currentProduct.id,
                    currentProduct,
                    formData.repositoryUrl,
                    formData.repoPath
                  );
                  
                  if (syncResult.success) {
                    if (syncResult.action === 'synced_from_file') {
                      // Product data was updated from repo file - reload products to get fresh data
                      console.log('[Products] Product metadata synced from repo file, reloading products');
                      const freshProducts = await loadProducts();
                      // Update the updatedProducts reference for subsequent code
                      updatedProducts.length = 0;
                      updatedProducts.push(...freshProducts);
                      
                      toast({
                        title: 'Product synced from repository',
                        description: 'Product metadata was loaded from the repository file.',
                      });
                    } else if (syncResult.action === 'written_to_file') {
                      console.log('[Products] Product metadata written to repo file');
                      // No need to reload - database is the source
                    }
                  } else {
                    console.warn('[Products] Product metadata sync had an issue:', syncResult.error);
                    // Don't fail - the product is saved in the database
                  }
                }
              }
            } else if (repoResult.error) {
              console.error('Failed to ensure repository:', repoResult.error);
              toast({
                title: 'Repository setup warning',
                description: `Product saved, but repository setup had an issue: ${repoResult.error}`,
                variant: 'destructive',
              });
            }
          }
        } catch (error: any) {
          console.error('Error ensuring repository:', error);
          // Don't fail the whole operation - product is saved
          toast({
            title: 'Repository setup warning',
            description: 'Product saved, but repository setup may need to be done manually.',
            variant: 'destructive',
          });
        }
      }
      
      // Update active product state if we're editing the currently active product
      if (isEditing && formData.id && activeProduct && activeProduct.id === formData.id) {
        // Find the updated product from the freshly loaded data
        const updatedProduct = updatedProducts.find(p => p.id === formData.id);
        if (updatedProduct) {
          // Update the active product with the new data from database
          // This ensures the active product state is synchronized with the latest data
          setActiveProduct(updatedProduct);
        } else if (previousRepoUrl && !nextRepoUrl) {
          // Product was deleted or repo URL was removed - clear active state
          setActiveProduct(null);
          setActiveRepoUrl(null);
        }
      } else if (
        isEditing &&
        previousRepoNormalized &&
        repoContext.normalizedRepoUrl &&
        repoContext.normalizedRepoUrl === previousRepoNormalized &&
        nextRepoNormalized !== previousRepoNormalized
      ) {
        // A different product's repo URL was changed and it was matching the active repo
        // Clear active state since the match is no longer valid
        setActiveProduct(null);
        setActiveRepoUrl(null);
      }
      
      // Reset form
      setShowForm(false);
      setIsEditing(false);
      setFormData({ ...emptyFormState });
      setCreateRepoError(null);
    } catch (error) {
      console.error('Error saving product:', error);
      setCreateRepoError(error instanceof Error ? error.message : 'Failed to save product');
      toast({
        title: 'Unable to save product',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingRepo(false);
    }
  };

  // Handle edit product
  const handleEdit = (product: Product) => {
    setFormData(toFormData(product));
    setIsEditing(true);
    setShowForm(true);
    // Scroll to form after it renders
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Handle delete product
  const handleDelete = (product: Product) => {
    setProductToDelete(product);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    try {
      // Delete via API
      await skillShopService.deleteProduct(productToDelete.id);
      
      // Reload products from database
      await loadProducts();

      if (productToDelete.repositoryUrl) {
        const productNormalized = normalizeRepoUrl(productToDelete.repositoryUrl);
        if (repoContext.normalizedRepoUrl && repoContext.normalizedRepoUrl === productNormalized) {
          setActiveProduct(null);
          setActiveRepoUrl(null);
        }
      } else if (activeProduct && activeProduct.id === productToDelete.id) {
        setActiveProduct(null);
        setActiveRepoUrl(null);
      }

      setShowDeleteDialog(false);
      setProductToDelete(null);
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Unable to delete product',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle cancel form
  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setFormData({ ...emptyFormState });
    setCreateRepoError(null);
  };

  // Handle successful product import
  const handleImportComplete = async (importedProduct: ProductMetadata) => {
    // Reload products to include the newly imported product
    const updatedProducts = await loadProducts();
    
    // Find and activate the imported product
    const product = updatedProducts.find(p => p.id === importedProduct.id);
    if (product) {
      setActiveProduct(product);
      toast({
        title: 'Product imported',
        description: `Successfully imported "${importedProduct.title}"`,
      });
    }
  };

  // Handle creating new product from import (when repo has no product.md)
  const handleCreateFromImport = (repoUrl: string, repoPath?: string) => {
    // Pre-fill the form with the repo URL and show the form
    setFormData({
      ...emptyFormState,
      repositoryUrl: repoUrl,
      repoPath: repoPath || null,
    });
    setIsEditing(false);
    setShowForm(true);
  };

  // Filter products based on search term
  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (product.productId ?? '').toLowerCase().includes(searchLower) ||
      product.title.toLowerCase().includes(searchLower) ||
      product.description.toLowerCase().includes(searchLower) ||
      product.owner.toLowerCase().includes(searchLower) ||
      (product.capabilities ?? []).some(c => c.toLowerCase().includes(searchLower)) ||
      (product.users ?? []).some(u => u.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Package style={{ marginRight: '12px', color: '#0066cc' }} />
            <h2 style={{ margin: 0 }}>Product Management</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Layout Toggle */}
            <TooltipProvider>
              <div className="flex items-center border rounded-lg p-1 bg-muted/30">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={layoutMode === 'list' ? 'default' : 'ghost'}
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setLayoutMode('list')}
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>List View</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={layoutMode === 'focus' ? 'default' : 'ghost'}
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setLayoutMode('focus')}
                      disabled={products.length === 0}
                    >
                      <Focus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Focus View</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={() => setShowImportDialog(true)}
                    disabled={!userOrganizationId}
                  >
                    <Download size={16} className="mr-2" />
                    Import
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Import product from repository</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/new-project')}
                  >
                    <FileCode size={16} className="mr-2" />
                    From Template
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Create new project from architecture template</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <button
              className="btn-compact"
              onClick={() => {
                setIsEditing(false);
                setFormData({ ...emptyFormState });
                setShowForm(true);
                // Scroll to form after it renders
                setTimeout(() => {
                  formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
            >
              <Plus size={16} style={{ marginRight: '8px' }} />
              New Product
            </button>
          </div>
        </div>

        {/* Focus View Layout */}
        {layoutMode === 'focus' && products.length > 0 && (
          <div 
            className="flex h-[calc(100vh-200px)] mt-4" 
            style={{ 
              marginLeft: '-24px', 
              marginRight: '-24px',
              border: '1px solid #ddd',
              background: '#f8f9fa',
              overflow: 'hidden'
            }}
          >
            {/* Product Browser - Left Side */}
            <div className="w-64 flex-shrink-0" style={{ background: '#fff', borderRight: '1px solid #ddd' }}>
              <ProductBrowser
                products={products}
                selectedProduct={focusedProduct}
                onSelectProduct={setFocusedProduct}
              />
            </div>

            {/* Focus View Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {focusedProduct ? (
                <ProductFocusView
                  product={focusedProduct}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onProductUpdated={loadProducts}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Select a product from the list to view details
                </div>
              )}
            </div>
          </div>
        )}

        {/* List View Layout */}
        {layoutMode === 'list' && (
          <>
            {/* Product Request Input */}
            <RequestInput
              title="Quick Product Request"
              description="Describe your product request in natural language."
              examples={[
                "Create a product for our mobile app",
                "Update PROD-001 owner to Platform Team",
                "Show me all products",
                "Add capability 'Analytics' to PROD-002"
              ]}
              value={requestProcessor.request}
              isProcessing={requestProcessor.isProcessing}
              isListening={requestProcessor.isListening}
              lastResponse={requestProcessor.lastResponse}
              speechSupported={requestProcessor.speechSupported}
              onValueChange={requestProcessor.setRequest}
              onProcess={requestProcessor.handleProcess}
              onVoiceInput={requestProcessor.toggleListening}
              onKeyPress={requestProcessor.handleKeyPress}
              data-walkthrough="quick-request"
            />

            {/* Filters */}
            <ProductFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />

            {/* Products List Header - includes organization badge and repo context */}
            <ProductListHeader
              filteredCount={filteredProducts.length}
              totalCount={products.length}
              isLoading={loadingProducts || isLoadingOrgs}
              onRefresh={loadProducts}
              organization={activeOrganization ? {
                id: activeOrganization.id,
                name: activeOrganization.name
              } : undefined}
              repoContext={{
                repoUrl: repoContext.repoUrl,
                repoPath: repoContext.repoPath
              }}
            />

            {/* Products List */}
            {loadingProducts ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Loader size={48} style={{ marginBottom: '16px', opacity: 0.5, animation: 'spin 1s linear infinite' }} />
                <p>Loading products from database...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Package size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>
                  {searchTerm 
                    ? 'No products match your search criteria.'
                    : 'No products found. Create your first product to get started.'
                  }
                </p>
                {!searchTerm && (
                  <button
                    className="btn"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({ ...emptyFormState });
                      setShowForm(true);
                      // Scroll to form after it renders
                      setTimeout(() => {
                        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    style={{ marginTop: '16px' }}
                  >
                    <Plus size={16} style={{ marginRight: '8px' }} />
                    Create Product
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px', marginTop: '24px' }}>
                {filteredProducts.map(product => {
                  // Use activeProduct.id for a definitive match instead of comparing normalized URLs
                  const isActive = !!activeProduct && activeProduct.id === product.id;
                  let localPath: string | undefined;
                  if (product.repositoryUrl) {
                    const basePath = getDefaultRepoRelativePath(product.repositoryUrl);
                    const repoPathSegment = product.repoPath
                      ? product.repoPath.replace(/^[\\/]+/, '').replace(/[\\/]+$/, '')
                      : '';
                    localPath = repoPathSegment ? `${basePath}/${repoPathSegment}` : basePath;
                  }
   
                  return (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isActive={isActive}
                      onSetActive={() => setActiveProduct(product)}
                      localPath={localPath}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}

        {showForm && (
          <div ref={formRef} style={{ marginTop: '24px' }}>
            <ProductForm
              formData={formData}
              onFormDataChange={(data) => setFormData(data)}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isEditing={isEditing}
              githubService={githubService}
              isGitHubConfigured={isGitHubConfigured}
              gitlabService={gitlabService}
              isGitLabConfigured={isGitLabConfigured}
              sourceControlProvider={sourceControlProvider}
              isCreatingRepo={isCreatingRepo}
              createRepoErrorMessage={createRepoError}
            />
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Product</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this product? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Import Product Dialog */}
        <ImportProductDialog
          open={showImportDialog}
          onOpenChange={setShowImportDialog}
          onImportComplete={handleImportComplete}
          onCreateNew={handleCreateFromImport}
          githubService={githubService}
          isGitHubConfigured={isGitHubConfigured}
          gitlabService={gitlabService}
          isGitLabConfigured={isGitLabConfigured}
          sourceControlProvider={sourceControlProvider}
          userOrganizationId={userOrganizationId}
          userId={user?.id}
          accessToken={sourceControlProvider === 'github' ? githubConfig?.accessToken : gitlabConfig?.accessToken}
          useSSH={sourceControlProvider === 'gitlab' && gitlabConfig?.useSSH}
        />
      </div>

      {/* Add CSS for the loader animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

